import { Controller } from "@hotwired/stimulus"
import { Sortable } from 'sortablejs'

// Connects to data-controller="field-transformations" on the transform drawer form.
// Drives the Map Values builder (mappings/conditions serialized to a hidden JSON
// field) and the Join Fields picker. The row/table cloning of the old inline editor
// is gone — each transform is now configured in a drawer and posted to the server.
export default class extends Controller {
    connect() {
        // Initialize SortableJS on any visible join fields containers
        this.initJoinFieldsSortables()

        // Seed the hidden mappings_json field for any existing map-values config
        this.element.querySelectorAll('[data-map-values-container]').forEach(c => this.serializeContainer(c))

        // Initialise the "at least one condition" UI state for every mapping
        this.refreshAllConditionStates()

        // Render the OR (between mappings) / AND (between conditions) dividers
        this.element.querySelectorAll('[data-map-values-container]').forEach(c => this.refreshDividers(c))

        // Block submit if any map-values mapping has no conditions (server validates too)
        this.boundValidateOnSubmit = this.validateMapValuesOnSubmit.bind(this)
        this.element.closest('form')?.addEventListener('submit', this.boundValidateOnSubmit)
    }

    disconnect() {
        this.element.closest('form')?.removeEventListener('submit', this.boundValidateOnSubmit)
    }

    validateFloorCeiling(event) {
        const input = event.target
        const container = input.closest('[data-field-transformations-target="paramsContainer"]')
            || input.closest('.mt-3.space-y-2')
        if (!container) return

        this.validateInContainer(container, input)
    }

    validateInContainer(container, _triggerInput) {
        let validationMessage = container.querySelector('[data-field-transformations-target="validationMessage"]')
            || container.querySelector('.validation-message')

        const constantInput = container.querySelector('input[name*="[constant]"]')
        const defaultValueInput = container.querySelector('input[name*="[default_value]"]')
        const floorInput = container.querySelector('input[name*="[floor]"]')
        const ceilingInput = container.querySelector('input[name*="[ceiling]"]')

        // A disabled constant belongs to the hidden "fixed number" panel and is not submitted.
        const constant = constantInput && !constantInput.disabled && constantInput.value ? parseFloat(constantInput.value) : null
        const defaultValue = defaultValueInput && defaultValueInput.value ? parseFloat(defaultValueInput.value) : null
        const floor = floorInput && floorInput.value ? parseFloat(floorInput.value) : null
        const ceiling = ceilingInput && ceilingInput.value ? parseFloat(ceilingInput.value) : null

        let errors = []

        if (constant !== null && (constant <= 0 || constant > 1000000)) {
            errors.push('Constant must be greater than 0 and up to 1 million')
        }
        if (defaultValue !== null && (defaultValue < 0 || defaultValue > 1000000)) {
            errors.push('Default value must be 0 or greater, up to 1 million')
        }
        if (floor !== null && floor < 0) {
            errors.push('Floor value cannot be less than 0')
        }
        if (floor !== null && ceiling !== null && floor >= ceiling) {
            errors.push('Floor must be less than ceiling')
        }

        if (constantInput) constantInput.classList.remove('border-red-500')
        if (defaultValueInput) defaultValueInput.classList.remove('border-red-500')
        if (floorInput) floorInput.classList.remove('border-red-500')
        if (ceilingInput) ceilingInput.classList.remove('border-red-500')

        if (errors.length > 0) {
            if (validationMessage) {
                validationMessage.textContent = errors.join('. ')
                validationMessage.classList.remove('hidden')
            }
            if (constant !== null && (constant <= 0 || constant > 1000000) && constantInput) {
                constantInput.classList.add('border-red-500')
            }
            if (defaultValue !== null && (defaultValue < 0 || defaultValue > 1000000) && defaultValueInput) {
                defaultValueInput.classList.add('border-red-500')
            }
            if (floor !== null && floor < 0 && floorInput) {
                floorInput.classList.add('border-red-500')
            }
            if (floor !== null && ceiling !== null && floor >= ceiling) {
                if (floorInput) floorInput.classList.add('border-red-500')
                if (ceilingInput) ceilingInput.classList.add('border-red-500')
            }
        } else if (validationMessage) {
            validationMessage.classList.add('hidden')
        }
    }

    // Math operand: show the chosen source's panel and enable only its inputs, so only it submits.
    toggleOperandSource(event) {
        const container = event.target.closest('[data-operand-source-container]')
        if (!container) return

        container.querySelectorAll('[data-operand-source-panel]').forEach(panel => {
            const active = panel.dataset.operandSourcePanel === event.target.value
            panel.classList.toggle('hidden', !active)
            panel.querySelectorAll('input, select').forEach(input => { input.disabled = !active })
        })
        this.validateInContainer(container, event.target)
    }

    // Math can't read the field it writes. A new Math's operand list is built before its field is
    // picked, so hide that field's option once it is. Options for unfilled fields stay disabled.
    hideOperandTarget(event) {
        const fieldId = event.target.value
        this.element.querySelectorAll('[name$="[operand_field]"] option[data-schema-field-id]').forEach(option => {
            const isTarget = option.dataset.schemaFieldId === fieldId
            option.hidden = isTarget
            option.disabled = isTarget || option.dataset.unfilled === 'true'
            if (isTarget && option.selected) option.closest('select').value = ''
        })
    }

    // --- Join Fields: sortable + checkbox logic ---

    initJoinFieldsSortables() {
        this.element.querySelectorAll('[data-join-fields-sortable]').forEach(el => {
            if (el.offsetParent !== null) {
                this.initJoinFieldsSortable(el.closest('[data-join-fields-container]'))
            }
        })
    }

    initJoinFieldsSortable(container) {
        if (!container) return
        const sortableEl = container.querySelector('[data-join-fields-sortable]')
        if (!sortableEl || sortableEl._sortableInstance) return

        sortableEl._sortableInstance = Sortable.create(sortableEl, {
            handle: '.drag-handle',
            animation: 150,
            onEnd: () => this.updateJoinFieldsInput(container)
        })
    }

    addJoinField(event) {
        event.preventDefault()
        const row = event.target.closest('[data-field-name]')
        const fieldName = row.dataset.fieldName
        const container = row.closest('[data-join-fields-container]')
        const sortableEl = container.querySelector('[data-join-fields-sortable]')

        const item = document.createElement('div')
        item.className = 'bg-blue-50 border border-blue-200 rounded px-3 py-1.5 mb-1 flex items-center justify-between cursor-move hover:bg-blue-100'
        item.dataset.fieldName = fieldName
        item.innerHTML = `
            <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-blue-400 drag-handle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16"/>
                </svg>
                <span class="text-sm font-medium text-blue-800" data-label></span>
            </div>
            <button type="button"
                    class="text-blue-400 hover:text-red-600 text-sm leading-none"
                    data-action="field-transformations#removeJoinField">
                &times;
            </button>
        `
        item.querySelector('[data-label]').textContent = fieldName
        sortableEl.appendChild(item)
        row.remove()

        this.initJoinFieldsSortable(container)
        this.updateJoinFieldsInput(container)
    }

    removeJoinField(event) {
        event.preventDefault()
        const selectedItem = event.target.closest('[data-join-fields-sortable] > [data-field-name]')
        if (!selectedItem) return
        const fieldName = selectedItem.dataset.fieldName
        const container = selectedItem.closest('[data-join-fields-container]')
        const availableEl = container.querySelector('[data-join-fields-available]')

        selectedItem.remove()

        const availableItem = document.createElement('div')
        availableItem.className = 'rounded px-3 py-1.5 mb-1 flex items-center justify-between hover:bg-gray-100 cursor-pointer'
        availableItem.dataset.action = 'click->field-transformations#addJoinField'
        availableItem.dataset.fieldName = fieldName
        availableItem.innerHTML = `
            <span class="text-sm text-gray-500" data-label></span>
            <span class="text-gray-400 text-xs">+ add</span>
        `
        availableItem.querySelector('[data-label]').textContent = fieldName
        availableEl.appendChild(availableItem)

        this.updateJoinFieldsInput(container)
    }

    updateJoinFieldsInput(container) {
        const sortableEl = container.querySelector('[data-join-fields-sortable]')
        const inputsEl = container.querySelector('[data-join-fields-inputs]')
        if (!sortableEl || !inputsEl) return

        const baseName = container.dataset.inputBaseName
        const fields = Array.from(sortableEl.querySelectorAll('[data-field-name]'))
            .map(el => el.dataset.fieldName)

        inputsEl.innerHTML = ''
        fields.forEach(name => {
            const input = document.createElement('input')
            input.type = 'hidden'
            input.name = `${baseName}[source_fields][]`
            input.value = name
            input.dataset.fieldTransformationsSelect = 'true'
            inputsEl.appendChild(input)
        })
    }

    // --- Map Values: build mappings + conditions, serialized to a hidden JSON field ---
    //
    // The visible inputs carry no `name`; on every change we read them via
    // data-mv-* hooks and write a JSON array to [data-map-values-json]. This
    // avoids Rails nested-attribute index juggling — the model expands the
    // JSON into transform_params["mappings"].

    addMapping(event) {
        event.preventDefault()
        const container = event.target.closest('[data-map-values-container]')
        if (!container) return

        const template = container.querySelector('[data-map-values-mapping-template]')
        const mappingsEl = container.querySelector('[data-map-values-mappings]')
        if (!template || !mappingsEl) return

        const clone = template.cloneNode(true)
        clone.removeAttribute('data-map-values-mapping-template')
        clone.classList.remove('hidden')

        mappingsEl.insertBefore(clone, template)
        this.refreshConditionState(clone)
        this.serializeContainer(container)
        this.refreshDividers(container)
    }

    removeMapping(event) {
        event.preventDefault()
        const mapping = event.target.closest('[data-map-values-mapping]')
        if (!mapping || mapping.hasAttribute('data-map-values-mapping-template')) return

        const container = mapping.closest('[data-map-values-container]')
        mapping.remove()
        this.serializeContainer(container)
        this.refreshDividers(container)
    }

    addCondition(event) {
        event.preventDefault()
        const mapping = event.target.closest('[data-map-values-mapping]')
        if (!mapping) return

        const conditionsEl = mapping.querySelector('[data-map-values-conditions]')
        const template = conditionsEl?.querySelector('[data-map-values-condition-template]')
        if (!conditionsEl || !template) return

        const clone = template.cloneNode(true)
        clone.removeAttribute('data-map-values-condition-template')
        clone.classList.remove('hidden')

        conditionsEl.insertBefore(clone, template)
        this.refreshConditionState(mapping)
        const container = mapping.closest('[data-map-values-container]')
        this.serializeContainer(container)
        this.refreshDividers(container)
    }

    removeCondition(event) {
        event.preventDefault()
        const condition = event.target.closest('[data-mv-condition]')
        if (!condition || condition.hasAttribute('data-map-values-condition-template')) return

        const mapping = condition.closest('[data-map-values-mapping]')
        // Keep at least one condition per mapping (the × is hidden in this case,
        // but guard here too in case it's triggered some other way).
        if (this.mappingConditionEls(mapping).length <= 1) return

        const container = condition.closest('[data-map-values-container]')
        condition.remove()
        this.refreshConditionState(mapping)
        this.serializeContainer(container)
        this.refreshDividers(container)
    }

    // --- Map Values: OR / AND relationship dividers ---
    //
    // Mappings are short-circuit OR (first match wins); conditions within a mapping
    // are AND. Make that visible with a centered, ruled "OR"/"AND" between cards and
    // condition rows. Rebuilt on every add/remove; dividers carry no inputs, so
    // serialization and validation (which key off [data-map-values-mapping] /
    // [data-mv-condition]) ignore them.
    refreshDividers(container) {
        if (!container) return
        const mappingsEl = container.querySelector('[data-map-values-mappings]')
        if (!mappingsEl) return

        mappingsEl.querySelectorAll(':scope > [data-mv-divider]').forEach(el => el.remove())
        const mappings = Array.from(mappingsEl.querySelectorAll('[data-map-values-mapping]'))
            .filter(el => !el.hasAttribute('data-map-values-mapping-template'))
        this.insertDividers(mappings, 'OR')

        mappings.forEach(mapping => {
            const conditionsEl = mapping.querySelector('[data-map-values-conditions]')
            if (!conditionsEl) return
            conditionsEl.querySelectorAll(':scope > [data-mv-divider]').forEach(el => el.remove())
            this.insertDividers(this.mappingConditionEls(mapping), 'AND')
        })
    }

    // Insert a centered, ruled divider labelled `label` before each item except the first.
    insertDividers(items, label) {
        const line = '<span class="flex-1 border-t border-gray-200"></span>'
        items.forEach((item, index) => {
            if (index === 0) return
            const divider = document.createElement('div')
            divider.setAttribute('data-mv-divider', '')
            divider.className = 'flex items-center gap-2 my-2 text-xs font-medium text-gray-400 select-none'
            divider.innerHTML = `${line}${label}${line}`
            item.parentNode.insertBefore(divider, item)
        })
    }

    // --- Map Values: "at least one condition" UI state ---

    // Non-template condition rows within a mapping.
    mappingConditionEls(mapping) {
        if (!mapping) return []
        return Array.from(mapping.querySelectorAll('[data-mv-condition]'))
            .filter(el => !el.hasAttribute('data-map-values-condition-template'))
    }

    // Hide the × when only one condition remains; show the hint when there are none.
    refreshConditionState(mapping) {
        if (!mapping || mapping.hasAttribute('data-map-values-mapping-template')) return

        const conditions = this.mappingConditionEls(mapping)
        const onlyOne = conditions.length <= 1
        conditions.forEach(c => {
            c.querySelector('[data-mv-remove-condition]')?.classList.toggle('hidden', onlyOne)
        })
        mapping.querySelector('[data-mv-no-conditions]')?.classList.toggle('hidden', conditions.length > 0)
    }

    refreshAllConditionStates() {
        this.element.querySelectorAll('[data-map-values-mapping]')
            .forEach(mapping => this.refreshConditionState(mapping))
    }

    // Belt-and-suspenders: block submit (and surface the hint) if any mapping
    // somehow has zero conditions. The server validates this regardless.
    validateMapValuesOnSubmit(event) {
        let invalid = false
        this.element.querySelectorAll('[data-map-values-mapping]').forEach(mapping => {
            if (mapping.hasAttribute('data-map-values-mapping-template')) return
            if (this.mappingConditionEls(mapping).length === 0) {
                this.refreshConditionState(mapping)
                invalid = true
            }
        })
        if (invalid) {
            event.preventDefault()
            this.element.querySelector('[data-mv-no-conditions]:not(.hidden)')
                ?.scrollIntoView({behavior: 'smooth', block: 'center'})
        }
    }

    toggleOperatorParams(event) {
        const select = event.target
        const condition = select.closest('[data-mv-condition]')
        if (!condition) return

        const group = this.paramGroupFor(select.value)
        condition.querySelectorAll('[data-mv-params]').forEach(el => {
            el.classList.toggle('hidden', el.dataset.mvParams !== group)
        })
    }

    serializeMapValues(event) {
        const container = event?.target?.closest?.('[data-map-values-container]')
        if (container) {
            this.serializeContainer(container)
        } else {
            this.element.querySelectorAll('[data-map-values-container]').forEach(c => this.serializeContainer(c))
        }
    }

    serializeContainer(container) {
        if (!container) return
        const jsonInput = container.querySelector('[data-map-values-json]')
        if (!jsonInput) return

        const mappings = []
        container.querySelectorAll('[data-map-values-mapping]').forEach(mappingEl => {
            if (mappingEl.hasAttribute('data-map-values-mapping-template')) return

            const conditions = []
            mappingEl.querySelectorAll('[data-mv-condition]').forEach(condEl => {
                if (condEl.hasAttribute('data-map-values-condition-template')) return

                const operator = condEl.querySelector('[data-mv-operator]')?.value || 'equals'
                const condition = {
                    field: condEl.querySelector('[data-mv-field]')?.value || '',
                    operator,
                    negate: condEl.querySelector('[data-mv-negate]')?.checked || false
                }

                const group = this.paramGroupFor(operator)
                if (group === 'values') {
                    const raw = condEl.querySelector('[data-mv-values]')?.value || ''
                    condition.values = raw.split('|').map(s => s.trim()).filter(s => s.length > 0)
                } else if (group === 'value') {
                    condition.value = condEl.querySelector('[data-mv-value]')?.value || ''
                } else if (group === 'range') {
                    condition.lower = condEl.querySelector('[data-mv-lower]')?.value || ''
                    condition.upper = condEl.querySelector('[data-mv-upper]')?.value || ''
                }

                conditions.push(condition)
            })

            mappings.push({
                output: mappingEl.querySelector('[data-mv-output]')?.value || '',
                conditions
            })
        })

        jsonInput.value = JSON.stringify(mappings)
    }

    paramGroupFor(operator) {
        if (['equals', 'contains', 'starts_with', 'ends_with'].includes(operator)) return 'values'
        if (['greater_than', 'greater_than_or_equal', 'less_than', 'less_than_or_equal'].includes(operator)) return 'value'
        if (operator === 'range') return 'range'
        return 'none'
    }
}
